import React, { useMemo, useCallback } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import { ArrowsClockwiseIcon, EyeIcon } from '@phosphor-icons/react';
import { DataTable } from '@/components/common/data-table/data-table';
import { useDataTable } from '@/components/common/data-table/use-data-table';
import { ServerSidePagination } from './ServerSidePagination';
import { DataTableToolbar } from '@/components/common/data-table/data-table-toolbar';
import { DataTableFacetedFilter } from '@/components/common/data-table/data-table-faceted-filters';
import {
  RexelFileProcessingJob,
  RexelFileProcessingJobStatus,
  PaginatedResponse,
  ProcessingJobsQuery,
} from '@/api/rexel/types';
import { useRetryProcessingJob } from '@/api/rexel/useRexelActions';

import { formatDistanceToNow } from 'date-fns';

interface ProcessingJobsTableProps {
  data: PaginatedResponse<RexelFileProcessingJob> | undefined;
  isLoading: boolean;
  filters: ProcessingJobsQuery;
  onFiltersChange: (filters: Partial<ProcessingJobsQuery>) => void;
}

const getStatusBadgeVariant = (status: RexelFileProcessingJobStatus) => {
  switch (status) {
    case RexelFileProcessingJobStatus.completed:
      return 'secondary';
    case RexelFileProcessingJobStatus.running:
      return 'success';
    case RexelFileProcessingJobStatus.failed:
      return 'destructive';
    case RexelFileProcessingJobStatus.pending:
      return 'outline';
    case RexelFileProcessingJobStatus.cancelled:
      return 'outline';
    default:
      return 'outline';
  }
};

const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) return '-';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
};

const ProcessingJobsTable: React.FC<ProcessingJobsTableProps> = ({
  data,
  isLoading,
  filters,
  onFiltersChange,
}) => {
  const { mutateAsync: retryJob } = useRetryProcessingJob();
  const handleRetryJob = useCallback(
    async (jobId: string) => {
      try {
        await retryJob({ jobId });
      } catch (error) {
        console.error('Failed to retry job:', error);
      }
    },
    [retryJob],
  );

  const columns: ColumnDef<RexelFileProcessingJob>[] = useMemo(
    () => [
      {
        accessorKey: 'blobName',
        header: 'File Name',
        cell: ({ row }) => (
          <div className="font-medium">{row.getValue('blobName')}</div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.getValue('status') as RexelFileProcessingJobStatus;
          return (
            <Badge variant={getStatusBadgeVariant(status)}>{status}</Badge>
          );
        },
      },
      {
        accessorKey: 'progress',
        header: 'Progress',
        cell: ({ row }) => {
          const progress = row.original.progress;
          const processedRows = row.original.processedRows;
          const totalRows = row.original.totalRows;

          if (progress !== null && progress !== undefined) {
            return (
              <div className="w-full">
                <div className="flex items-center justify-between text-sm">
                  <span>{progress}%</span>
                  <span className="text-muted-foreground">
                    {processedRows?.toLocaleString() || 0}/
                    {totalRows?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="w-full bg-secondary rounded-full h-2 mt-1">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            );
          }
          return '-';
        },
      },
      {
        accessorKey: 'retryCount',
        header: 'Retries',
        cell: ({ row }) => {
          const retryCount = row.getValue('retryCount') as number;
          const maxRetries = row.original.maxRetries;
          return `${retryCount}/${maxRetries}`;
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => {
          const date = new Date(row.getValue('createdAt') as string);
          return (
            <div className="text-sm">
              <div>{formatDistanceToNow(date, { addSuffix: true })}</div>
              <div className="text-muted-foreground text-xs">
                {date.toLocaleString()}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'durationSeconds',
        header: 'Duration',
        cell: ({ row }) => {
          return formatDuration(row.original.durationSeconds || null);
        },
      },
      {
        id: 'actions',
        cell: ({ row }) => {
          const job = row.original;
          const canRetry = job.status === RexelFileProcessingJobStatus.failed;

          return (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // TODO: Open job details modal
                  console.log('View job details:', job.id);
                }}
              >
                <EyeIcon className="h-4 w-4" />
              </Button>
              {canRetry && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRetryJob(job.id)}
                >
                  <ArrowsClockwiseIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [handleRetryJob],
  );

  const statusOptions = [
    { label: 'Completed', value: RexelFileProcessingJobStatus.completed },
    { label: 'Running', value: RexelFileProcessingJobStatus.running },
    { label: 'Failed', value: RexelFileProcessingJobStatus.failed },
    { label: 'Pending', value: RexelFileProcessingJobStatus.pending },
    { label: 'Cancelled', value: RexelFileProcessingJobStatus.cancelled },
  ];

  const table = useDataTable({
    columns,
    data: data?.data || [],
    options: {
      initialState: {
        pagination: { pageSize: (filters.limit ?? 25) as number },
      },
    },
  });

  return (
    <div className="space-y-4">
      <DataTableToolbar
        table={table}
        searchPlaceholder="Search by file name..."
        searchValue={filters.fileName || ''}
        onSearchChange={(value) => onFiltersChange({ fileName: value })}
        facetedFilters={
          <DataTableFacetedFilter
            column={table.getColumn('status')}
            title="Status"
            options={statusOptions}
          />
        }
      />

      <DataTable
        table={table}
        isLoading={isLoading}
        emptyMessage="No processing jobs found"
      />

      {data && (
        <ServerSidePagination
          pagination={data.pagination}
          onPageChange={(page) => onFiltersChange({ page: page as number })}
          onPageSizeChange={(limit) =>
            onFiltersChange({ limit: limit as number })
          }
        />
      )}
    </div>
  );
};

export default ProcessingJobsTable;
